import React from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { PackageOpen, ClipboardCheck, Ship, HeartHandshake, Users, Recycle } from 'lucide-react';
export function HowItWorksPage() {
  return <>
      {/* Hero Section */}
      <Section className="py-20" background="offwhite" data-id="element-1230">
        <div className="text-center mb-12" data-id="element-1231">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-1232">
            How Farasha Medical Aid Works
          </h1>
          <p className="text-xl font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-1233">
            Bridging the gap between medical surplus and scarcity through
            compassionate action and cultural understanding.
          </p>
        </div>
        <div className="mt-8 flex justify-center" data-id="element-1234">
          <div className="bg-white p-6 rounded-xl shadow-md max-w-3xl" data-id="element-1235">
            <p className="text-lg font-inter text-neutral-charcoal" data-id="element-1236">
              Farasha Medical Aid is a nonprofit founded to reduce medical waste
              and bridge critical gaps in healthcare for underserved Coptic
              Orthodox communities in Egypt. Our work honors both environmental
              responsibility and religious-cultural solidarity.
            </p>
          </div>
        </div>
      </Section>
      {/* Process Steps */}
      <Section background="white" data-id="element-1237">
        <div className="mb-16" data-id="element-1238">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-12 text-center" data-id="element-1239">
            Our Process
          </h2>
          {/* Step 1 */}
          <div className="grid md:grid-cols-5 gap-12 items-center mb-20" data-id="element-1240">
            <div className="md:col-span-2 order-2 md:order-1" data-id="element-1241">
              <div className="rounded-2xl overflow-hidden shadow-lg" data-id="element-1242">
                <img src="https://images.unsplash.com/photo-1603398938378-e54eab446dde?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80" alt="Medical supplies collection" className="w-full h-full object-cover" data-id="element-1243" />
              </div>
            </div>
            <div className="md:col-span-3 order-1 md:order-2" data-id="element-1244">
              <div className="flex items-center mb-4" data-id="element-1245">
                <div className="bg-primary-royal-blue rounded-full p-3 mr-4" data-id="element-1246">
                  <PackageOpen size={24} className="text-white" data-id="element-1247" />
                </div>
                <h3 className="text-2xl font-playfair font-bold text-primary-deep-blue" data-id="element-1248">
                  1. Collecting Surplus Medical Supplies
                </h3>
              </div>
              <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-1249">
                We partner with hospitals, clinics, dental offices, and other
                healthcare providers in the U.S. to recover clean, unused,
                unexpired supplies that are often prematurely discarded.
              </p>
              <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-1250">
                Working with domestic medical facilities helps redirect
                resources that would otherwise end up in landfills—reducing
                waste and giving these supplies a second, life-saving purpose.
              </p>
              <p className="text-lg font-inter text-neutral-charcoal" data-id="element-1251">
                Organizations like Project C.U.R.E., MedWish, and Afya
                Foundation demonstrate this model's effectiveness in
                distributing surplus supplies globally.
              </p>
            </div>
          </div>
          {/* Step 2 */}
          <div className="grid md:grid-cols-5 gap-12 items-center mb-20" data-id="element-1252">
            <div className="md:col-span-3" data-id="element-1253">
              <div className="flex items-center mb-4" data-id="element-1254">
                <div className="bg-primary-royal-blue rounded-full p-3 mr-4" data-id="element-1255">
                  <ClipboardCheck size={24} className="text-white" data-id="element-1256" />
                </div>
                <h3 className="text-2xl font-playfair font-bold text-primary-deep-blue" data-id="element-1257">
                  2. Sorting and Matching Needs
                </h3>
              </div>
              <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-1258">
                Donated supplies are meticulously sorted, inspected, and
                catalogued to ensure safety and appropriateness.
              </p>
              <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-1259">
                We connect directly with Coptic Orthodox clinics, monasteries,
                and community healthcare centers in Egypt, assessing their
                specific needs—whether it's bandages, surgical kits, basic
                medications, or dental supplies.
              </p>
              <p className="text-lg font-inter text-neutral-charcoal" data-id="element-1260">
                This ensures each shipment is tailored and meaningful, rather
                than generic.
              </p>
            </div>
            <div className="md:col-span-2" data-id="element-1261">
              <div className="rounded-2xl overflow-hidden shadow-lg" data-id="element-1262">
                <img src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80" alt="Medical supplies being sorted" className="w-full h-full object-cover" data-id="element-1263" />
              </div>
            </div>
          </div>
          {/* Step 3 */}
          <div className="grid md:grid-cols-5 gap-12 items-center" data-id="element-1264">
            <div className="md:col-span-2 order-2 md:order-1" data-id="element-1265">
              <div className="rounded-2xl overflow-hidden shadow-lg" data-id="element-1266">
                <img src="https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80" alt="Medical supplies being delivered" className="w-full h-full object-cover" data-id="element-1267" />
              </div>
            </div>
            <div className="md:col-span-3 order-1 md:order-2" data-id="element-1268">
              <div className="flex items-center mb-4" data-id="element-1269">
                <div className="bg-primary-royal-blue rounded-full p-3 mr-4" data-id="element-1270">
                  <Ship size={24} className="text-white" data-id="element-1271" />
                </div>
                <h3 className="text-2xl font-playfair font-bold text-primary-deep-blue" data-id="element-1272">
                  3. Redistribution and Impact
                </h3>
              </div>
              <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-1273">
                Supplies are shipped efficiently to designated recipients in
                Egypt, where they immediately support:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-lg font-inter text-neutral-charcoal" data-id="element-1274">
                <li data-id="element-1275">
                  Monasteries providing medical assistance in rural or
                  underserved regions
                </li>
                <li data-id="element-1276">Local clinics facing resource shortages</li>
                <li data-id="element-1277">Coptic hospitals serving marginalized patients</li>
              </ul>
              <p className="text-lg font-inter text-neutral-charcoal" data-id="element-1278">
                This approach strengthens local healthcare capacity and supports
                culturally aligned institutions that understand the unique
                challenges faced by Coptic communities.
              </p>
            </div>
          </div>
        </div>
      </Section>
      {/* Why Focus on Coptic Communities */}
      <Section background="sand" data-id="element-1279">
        <div className="mb-12" data-id="element-1280">
          <div className="flex items-center justify-center mb-8" data-id="element-1281">
            <div className="bg-primary-royal-blue rounded-full p-3 mr-4" data-id="element-1282">
              <Users size={24} className="text-white" data-id="element-1283" />
            </div>
            <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue" data-id="element-1284">
              Why Focus on Coptic Orthodox Communities?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12" data-id="element-1285">
            <div data-id="element-1286">
              <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-1287">
                Copts are Egypt's largest Christian minority, estimated at
                around 10% of the population.
              </p>
              <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-1288">
                Historically and currently, they face systemic discrimination,
                including reduced access to services in housing, education, and
                healthcare.
              </p>
              <p className="text-lg font-inter text-neutral-charcoal" data-id="element-1289">
                Coptic monasteries and church-run clinics often serve as trusted
                healthcare providers, offering compassionate and respectful care
                to their communities—especially where state support is lacking.
              </p>
            </div>
            <div data-id="element-1290">
              <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-1291">
                By directing support towards these institutions, Farasha helps
                uphold dignity, strengthens culturally competent care, and
                ensures aid reaches those who need it most.
              </p>
              <div className="bg-white p-6 rounded-xl shadow-md mt-6" data-id="element-1292">
                <h4 className="font-playfair font-bold text-xl text-primary-deep-blue mb-3" data-id="element-1293">
                  Our Impact
                </h4>
                <p className="font-inter text-neutral-charcoal" data-id="element-1294">
                  Through our targeted approach, we're able to provide
                  culturally-sensitive healthcare support to communities that
                  often fall through the cracks of traditional aid programs,
                  ensuring that medical supplies reach those who truly need
                  them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>
      {/* Ethical & Environmental Benefits */}
      <Section background="white" data-id="element-1295">
        <div className="mb-12" data-id="element-1296">
          <div className="flex items-center justify-center mb-8" data-id="element-1297">
            <div className="bg-primary-royal-blue rounded-full p-3 mr-4" data-id="element-1298">
              <Recycle size={24} className="text-white" data-id="element-1299" />
            </div>
            <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue" data-id="element-1300">
              Ethical & Environmental Benefits
            </h2>
          </div>
          <div className="max-w-4xl mx-auto" data-id="element-1301">
            <p className="text-lg font-inter text-neutral-charcoal mb-6" data-id="element-1302">
              Redirecting usable medical supplies prevents waste and supports
              global resource equity. It aligns with principles of stewardship,
              sustainability, and ethical responsibility, giving medical
              materials a second life in the hands of the vulnerable.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-8" data-id="element-1303">
              <div className="bg-neutral-offwhite p-6 rounded-xl" data-id="element-1304">
                <h4 className="font-playfair font-bold text-xl text-primary-deep-blue mb-3" data-id="element-1305">
                  Environmental Impact
                </h4>
                <p className="font-inter text-neutral-charcoal" data-id="element-1306">
                  Reducing medical waste that would otherwise end up in
                  landfills, contributing to a more sustainable healthcare
                  system.
                </p>
              </div>
              <div className="bg-neutral-offwhite p-6 rounded-xl" data-id="element-1307">
                <h4 className="font-playfair font-bold text-xl text-primary-deep-blue mb-3" data-id="element-1308">
                  Resource Optimization
                </h4>
                <p className="font-inter text-neutral-charcoal" data-id="element-1309">
                  Ensuring valuable medical supplies are used to their full
                  potential rather than being prematurely discarded.
                </p>
              </div>
              <div className="bg-neutral-offwhite p-6 rounded-xl" data-id="element-1310">
                <h4 className="font-playfair font-bold text-xl text-primary-deep-blue mb-3" data-id="element-1311">
                  Global Equity
                </h4>
                <p className="font-inter text-neutral-charcoal" data-id="element-1312">
                  Addressing healthcare disparities by redirecting resources
                  from areas of surplus to areas of need.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>
      {/* Summary & CTA */}
      <Section background="deep-blue" data-id="element-1313">
        <div className="text-center" data-id="element-1314">
          <h2 className="text-3xl font-playfair font-bold mb-6" data-id="element-1315">
            Our Mission in Action
          </h2>
          <p className="text-xl font-inter mb-8 max-w-3xl mx-auto" data-id="element-1316">
            Farasha Medical Aid rescues unused medical supplies from U.S.
            providers and delivers them to Coptic Orthodox clinics and
            monasteries in Egypt—organizations rooted in compassion and trusted
            by communities facing systemic barriers. By bridging surplus to
            need, we amplify healing, preserve dignity, and extend hope where it
            matters most.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1317">
            <Button href="/donate" size="lg" data-id="element-1318">
              Support Our Work
            </Button>
            <Button href="/get-involved" variant="secondary" size="lg" data-id="element-1319">
              Get Involved
            </Button>
          </div>
        </div>
      </Section>
    </>;
}